<template>
  <div class="report-list-view" v-if="canView" v-cloak>
    <card :title="$t('common.reports')" id="reports">
      <!-- Filters -->
      <div class="filters">
        <button type="button" class="pill" :class="{ active: selectedGroup === 'ALL' }" @click="onSelectGroup('ALL')">
          {{ $t("common.all") || "Toate" }}
        </button>

        <button
          v-for="g in groupTabs"
          :key="g.code"
          type="button"
          class="pill"
          :class="{ active: selectedGroup === g.code }"
          @click="onSelectGroup(g.code)"
        >
          {{ g.name }}
        </button>
      </div>

      <!-- Meta -->
      <div class="listing-meta">
        <span> Listing {{ shownCount }} of {{ totalCount }} results. </span>
        <a href="" @click.prevent="resetFilters" class="reset-link">Reset</a>
      </div>

      <!-- Grid -->
      <section class="reports-grid" aria-label="Report list">
        <button v-for="r in shownReports" :key="r.id" type="button" class="report-card" @click="openReport(r)">
          <img class="card-icon" :src="require('@/assets/images/desktop/icons/brand-green/report.svg')" alt="" />
          <div class="report-info">
            <span class="title">{{ r.name }}</span>
            <span class="description">{{ r.description }}</span>
          </div>
        </button>
      </section>
    </card>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Forbidden from "@/pages/errors/Forbidden.vue";
import { canView, permissions, permissionTypes } from "@/helpers/Permissions";

export default {
  name: "ReportList",
  components: { Forbidden },
  data() {
    return {
      reports: [],
      selectedGroup: "ALL",
      pageSize: 25,
    };
  },
  computed: {
    config() {
      return { permission: permissions.REPORTS, permission_type: permissionTypes.module };
    },
    canView() {
      return canView(this);
    },
    // Unique group tabs built from API payload
    groupTabs() {
      const map = new Map();
      for (const r of this.reports) {
        if (r.group && r.group.code && !map.has(r.group.code)) {
          map.set(r.group.code, { code: r.group.code, name: r.group.name });
        }
      }
      return Array.from(map.values());
    },
    filteredReports() {
      const byGroup =
        this.selectedGroup === "ALL" ? this.reports : this.reports.filter((r) => r.group && r.group.code === this.selectedGroup);
      return byGroup;
    },
    shownReports() {
      return this.filteredReports.slice(0, this.pageSize);
    },
    shownCount() {
      return this.shownReports.length;
    },
    totalCount() {
      return this.filteredReports.length;
    },
  },
  methods: {
    ...mapActions({
      getReportGroups: "reporting/getReportGroups",
      get: "reporting/getReports",
    }),
    async load() {
      try {
        const resp = await this.get({});
        this.reports = Array.isArray(resp.data) ? resp.data : [];
      } catch (e) {
        this.reports = [];
      }
    },
    onSelectGroup(code) {
      this.selectedGroup = code;
    },
    resetFilters() {
      this.selectedGroup = "ALL";
    },
    openReport(report) {
      this.$router
        .push({
          name: "report-view",
          params: {
            report,
          },
        })
        .catch((e) => {
          console.error("Navigation error:", e);
        });
    },
  },
  async mounted() {
    // If you still need groups list elsewhere, keep this call
    this.getReportGroups({ limit: 999 }).catch(() => {});
    await this.load();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global/global";

.report-list-view {
  height: 100%;
  width: 100%;

  #reports {
    height: 100%;
  }
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.pill {
  border: none;
  background: #fff;
  color: $brand;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 40px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.10);
}
.pill.active {
  background: $gray-8;
  border: 1px solid $gray-30;
  color: $gray-60;
  box-shadow: none;
}

.listing-meta {
  display: flex;
  align-items: baseline;
  gap: 10px;
  color: #6a7a72;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.02em;
  line-height: 15px;
  margin-bottom: 24px;
}
.reset-link {
  color: $brand;
  font-weight: 700;
  text-decoration: none;
}
.reset-link:hover {
  text-decoration: underline;
}

/* Grid of cards */
.reports-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(220px, 1fr));
  gap: 24px 24px;
}
@media (max-width: 1200px) {
  .reports-grid {
    grid-template-columns: repeat(3, minmax(220px, 1fr));
  }
}
@media (max-width: 860px) {
  .reports-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
}
@media (max-width: 560px) {
  .reports-grid {
    grid-template-columns: 1fr;
  }
}

/* Card */
.report-card {
  display: flex;
  align-items: flex-start; // align top so title sits in the same row as the icon
  gap: 16px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e7ece8;
  border-radius: 8px;
  padding: 16px 16px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.10);
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}
.report-card:hover {
  border: 1px solid $brand;
}

.card-icon {
  width: 32px;
  height: 32px;
  margin: 0;
  flex-shrink: 0;
}

.report-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.title {
  color: $gray-90;
  font-weight: 700;
  font-size: 12px;
  margin: 0;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: normal;
}

.description {
  color: $gray-70;
  font-weight: 400;
  font-size: 12px;
  white-space: pre-line;
  line-height: 1.25;
}
</style>
